import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";

import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import FollowButton from "../components/FollowButton";
import LeftMenu from "../components/LeftMenu";
import { TEAMS_DATABASE, searchTeams } from "../data/teamsDatabase";
import apiService from "../services/api";
import { sportsDbFetch } from "../utils/sportsDbCache";
import {
  getFavoriteTeams,
  isTeamFollowed,
  followTeam,
  unfollowTeam,
} from "../utils/sportsPreferences";
import webPlayerStorage from "../utils/webPlayerStorage";

const INITIAL_SHOW = 30;

// Sport filters
const SPORT_FILTERS = [
  { label: "All", sport: null, icon: "fas fa-globe" },
  { label: "Soccer", sport: "Soccer", icon: "fas fa-futbol" },
  { label: "NFL", sport: "American Football", icon: "fas fa-football-ball" },
  { label: "NBA", sport: "Basketball", icon: "fas fa-basketball-ball" },
  { label: "NHL", sport: "Ice Hockey", icon: "fas fa-hockey-puck" },
  { label: "MLB", sport: "Baseball", icon: "fas fa-baseball-ball" },
  { label: "NCAAF", sport: "NCAAF", icon: "fas fa-football-ball" },
  { label: "Rugby", sport: "Rugby", icon: "fas fa-football-ball" },
  { label: "MMA", sport: "Fighting", icon: "fas fa-fist-raised" },
  {
    label: "Motorsport",
    sport: "Motorsport",
    icon: "fas fa-flag-checkered",
  },
];

// TheSportsDB league IDs for batch team color fetching
const SPORTSDB_LEAGUES = [
  { id: 4328, label: "Premier League" },
  { id: 4335, label: "La Liga" },
  { id: 4331, label: "Bundesliga" },
  { id: 4332, label: "Serie A" },
  { id: 4334, label: "Ligue 1" },
  { id: 4391, label: "NFL" },
  { id: 4387, label: "NBA" },
  { id: 4380, label: "NHL" },
  { id: 4424, label: "MLB" },
  { id: 4346, label: "MLS" },
];

const WebPlayerSportsTeams = () => {
  const navigate = useNavigate();
  const playlist = webPlayerStorage.getPlaylist();
  const profile = webPlayerStorage.getProfile();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeSport, setActiveSport] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [followedTeams, setFollowedTeams] = useState(() => getFavoriteTeams());
  const [liveGames, setLiveGames] = useState([]);
  const [teamColors, setTeamColors] = useState({});
  const [loadingLive, setLoadingLive] = useState(true);
  const [contextMenu, setContextMenu] = useState(null); // { teamName, x, y }

  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const longPressTimer = useRef(null);
  const didLongPress = useRef(false);
  const pointerPos = useRef({ x: 0, y: 0 });

  const LONG_PRESS_MS = 500;

  // Embla carousels
  const [myTeamsRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: false,
    dragFree: true,
  });

  const [liveNowRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: false,
    dragFree: true,
  });

  const [recommendedRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: false,
    dragFree: true,
  });

  // Fetch live games from sports home API
  useEffect(() => {
    const fetchLiveGames = async () => {
      try {
        const response = await apiService.getSportsHome();
        const carousel = response?.data?.carousel || [];

        // Parse games to extract team data + live status
        const games = carousel
          .map((event) => {
            const isAPISports = event.teams && event.scores && event.status;
            const isLive = isAPISports
              ? event.status?.short !== "NS" &&
                event.status?.short !== "TBD" &&
                event.status?.short !== "PST" &&
                event.status?.short !== "FT" &&
                event.status?.short !== "AOT" &&
                event.status?.short !== "AET"
              : false;

            const isUpcoming = isAPISports
              ? event.status?.short === "NS" || event.status?.short === "TBD"
              : false;

            const homeTeam = isAPISports
              ? event.teams.home.name
              : event.homeTeam;
            const awayTeam = isAPISports
              ? event.teams.away.name
              : event.awayTeam;
            const homeTeamLogo = isAPISports
              ? event.teams.home.logo
              : event.homeTeamLogo;
            const awayTeamLogo = isAPISports
              ? event.teams.away.logo
              : event.awayTeamLogo;

            let homeScore = null;
            let awayScore = null;

            if (isAPISports) {
              const hs = event.scores?.home || event.goals?.home;
              const as = event.scores?.away || event.goals?.away;

              if (typeof hs === "object" && hs !== null) {
                homeScore = hs.total ?? hs.current ?? null;
              } else {
                homeScore = hs ?? null;
              }

              if (typeof as === "object" && as !== null) {
                awayScore = as.total ?? as.current ?? null;
              } else {
                awayScore = as ?? null;
              }
            }

            const league = event.league?.name || event.strLeague || "";
            const eventDate = event.date
              ? new Date(event.date)
              : event.strTimestamp
                ? new Date(event.strTimestamp)
                : null;
            const statusText = isAPISports ? event.status?.long || "" : "";

            return {
              id: event.mediaId || event.id,
              homeTeam,
              awayTeam,
              homeTeamLogo,
              awayTeamLogo,
              homeScore,
              awayScore,
              league,
              isLive,
              isUpcoming,
              statusText,
              eventDate,
              sport: event.categoryName || event.sport || "Sports",
            };
          })
          .filter((g) => g.homeTeam && g.awayTeam);

        setLiveGames(games);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch live games:", err);
      } finally {
        setLoadingLive(false);
      }
    };

    fetchLiveGames();
  }, []);

  // Fetch team colors from TheSportsDB (batch per league)
  useEffect(() => {
    const fetchTeamColors = async () => {
      const colorMap = {};

      const promises = SPORTSDB_LEAGUES.map(async (league) => {
        try {
          const data = await sportsDbFetch(
            `lookup_all_teams.php?id=${league.id}`
          );

          if (data?.teams) {
            data.teams.forEach((t) => {
              if (t.strTeam) {
                colorMap[t.strTeam.toLowerCase()] = {
                  color1: t.strColour1 || null,
                  color2: t.strColour2 || null,
                  color3: t.strColour3 || null,
                  fanart: t.strTeamFanart1 || null,
                  badge: t.strBadge || null,
                };
              }

              // Also store alternate names
              if (t.strAlternate) {
                t.strAlternate.split("/").forEach((alt) => {
                  const trimmed = alt.trim().toLowerCase();

                  if (trimmed) {
                    colorMap[trimmed] = colorMap[t.strTeam.toLowerCase()];
                  }
                });
              }
            });
          }
        } catch {
          // Silently skip failed league fetches
        }
      });

      await Promise.all(promises);
      setTeamColors(colorMap);
    };

    fetchTeamColors();
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const handleFollowChange = useCallback(() => {
    setFollowedTeams(getFavoriteTeams());
  }, []);

  // Long-press to open context menu
  const handleCardPointerDown = useCallback((e, teamName) => {
    didLongPress.current = false;
    pointerPos.current = { x: e.clientX, y: e.clientY };
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      setContextMenu({
        teamName,
        x: pointerPos.current.x,
        y: pointerPos.current.y,
      });
    }, LONG_PRESS_MS);
  }, []);

  const handleCardPointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Close context menu when clicking elsewhere
  useEffect(() => {
    if (!contextMenu) return;

    const close = () => setContextMenu(null);

    window.addEventListener("pointerdown", close);

    return () => window.removeEventListener("pointerdown", close);
  }, [contextMenu]);

  const handleContextFollow = useCallback(() => {
    if (!contextMenu) return;

    const { teamName } = contextMenu;
    const followed = isTeamFollowed(teamName);

    if (followed) {
      unfollowTeam(teamName);
      toast.info(`Unfollowed ${teamName}`, {
        position: "bottom-center",
        autoClose: 2000,
      });
    } else {
      followTeam(teamName);
      toast.success(`Following ${teamName}`, {
        position: "bottom-center",
        autoClose: 2000,
      });
    }

    setFollowedTeams(getFavoriteTeams());
    setContextMenu(null);
  }, [contextMenu]);

  const handleContextViewTeam = useCallback(() => {
    if (!contextMenu) return;

    navigate(
      `/web-player/sports/team/${encodeURIComponent(contextMenu.teamName)}`
    );
    setContextMenu(null);
  }, [contextMenu, navigate]);

  const handleCardClick = useCallback(
    (team) => {
      if (didLongPress.current) {
        didLongPress.current = false;

        return;
      }

      navigate(
        `/web-player/sports/team/${encodeURIComponent(team.name || team)}`
      );
    },
    [navigate]
  );

  // Get team color for a team name
  const getTeamColor = useCallback(
    (teamName) => {
      if (!teamName) return null;

      const lower = teamName.toLowerCase();
      const exact = teamColors[lower];

      if (exact) return exact;

      // Try partial match
      const keys = Object.keys(teamColors);

      for (const key of keys) {
        if (lower.includes(key) || key.includes(lower)) {
          return teamColors[key];
        }
      }

      return null;
    },
    [teamColors]
  );

  // My followed teams with full data
  const myTeamsData = useMemo(
    () =>
      followedTeams
        .map((name) =>
          TEAMS_DATABASE.find(
            (t) => t.name.toLowerCase() === name.toLowerCase()
          )
        )
        .filter(Boolean),
    [followedTeams]
  );

  // Teams currently live
  const liveTeamNames = useMemo(() => {
    const names = new Set();

    liveGames
      .filter((g) => g.isLive)
      .forEach((g) => {
        names.add(g.homeTeam?.toLowerCase());
        names.add(g.awayTeam?.toLowerCase());
      });

    return names;
  }, [liveGames]);

  // Live games involving followed teams
  const myLiveGames = useMemo(
    () =>
      liveGames.filter(
        (g) =>
          g.isLive &&
          (followedTeams.some(
            (ft) => ft.toLowerCase() === g.homeTeam?.toLowerCase()
          ) ||
            followedTeams.some(
              (ft) => ft.toLowerCase() === g.awayTeam?.toLowerCase()
            ))
      ),
    [liveGames, followedTeams]
  );

  // Recommended teams: from same leagues as followed teams, not yet followed
  const recommendedTeams = useMemo(() => {
    if (followedTeams.length === 0) return [];

    const followedLeagues = new Set(
      myTeamsData.map((t) => t.league).filter(Boolean)
    );
    const followedSet = new Set(followedTeams.map((n) => n.toLowerCase()));

    return TEAMS_DATABASE.filter(
      (t) =>
        followedLeagues.has(t.league) && !followedSet.has(t.name.toLowerCase())
    ).slice(0, 20);
  }, [followedTeams, myTeamsData]);

  // Filtered teams list
  const filteredTeams = useMemo(() => {
    if (debouncedQuery.trim().length >= 2) {
      const results = searchTeams(debouncedQuery, 200);

      if (activeSport) {
        return results.filter((t) => t.sport === activeSport);
      }

      return results;
    }

    if (activeSport) {
      return TEAMS_DATABASE.filter((t) => t.sport === activeSport);
    }

    return [...TEAMS_DATABASE];
  }, [debouncedQuery, activeSport]);

  const displayedTeams = showAll
    ? filteredTeams
    : filteredTeams.slice(0, INITIAL_SHOW);

  const handleTeamClick = (team) => {
    navigate(
      `/web-player/sports/team/${encodeURIComponent(team.name || team)}`
    );
  };

  const handleGameClick = (game) => {
    navigate(`/web-player/sports/event/${game.id}`, {
      state: { event: game },
    });
  };

  // Helper: team card color style
  const getCardColorStyle = (teamName) => {
    const tc = getTeamColor(teamName);

    if (!tc?.color1) return {};

    const c = tc.color1.startsWith("#") ? tc.color1 : `#${tc.color1}`;

    return {
      borderTop: `3px solid ${c}`,
      background: `linear-gradient(180deg, ${c}15 0%, transparent 60%)`,
    };
  };

  const isTeamLive = (teamName) => liveTeamNames.has(teamName?.toLowerCase());

  // Get next game for a team
  const getNextGame = (teamName) => {
    const lower = teamName?.toLowerCase();

    return liveGames.find(
      (g) =>
        g.homeTeam?.toLowerCase() === lower ||
        g.awayTeam?.toLowerCase() === lower
    );
  };

  return (
    <div className="min-h-screen relative">
      <LeftMenu playlist={playlist} profile={profile} />

      <div className="pl-20 relative z-10">
        {/* ─── HERO SECTION ─── */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-8 pt-10 pb-8 relative">
            <div className="flex items-start justify-between">
              <div>
                <button
                  className="text-white/40 hover:text-white/70 transition-colors mb-4 text-sm"
                  onClick={() => navigate(-1)}
                >
                  <i className="fas fa-arrow-left mr-2" />
                  Back to Sports
                </button>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
                  Teams
                </h1>
                <p className="text-white/40 text-lg mb-6">
                  Discover and follow your favorite teams
                </p>
                {/* Quick stats */}
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-white">
                      {followedTeams.length}
                    </span>
                    <span className="text-white/30 text-sm">following</span>
                  </div>
                  {liveGames.filter((g) => g.isLive).length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-2xl font-bold text-white">
                        {liveGames.filter((g) => g.isLive).length}
                      </span>
                      <span className="text-white/30 text-sm">live now</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-white/40">
                      {TEAMS_DATABASE.length}+
                    </span>
                    <span className="text-white/30 text-sm">teams</span>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="relative w-80 mt-6">
                <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                <input
                  className="w-full bg-white/[0.06] border border-white/[0.1] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.08] transition-all backdrop-blur-sm"
                  placeholder="Search teams, leagues..."
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    onClick={() => setSearchQuery("")}
                  >
                    <i className="fas fa-times text-xs" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-8 pb-12">
          {/* ─── LIVE NOW STRIP ─── */}
          {!loadingLive && liveGames.filter((g) => g.isLive).length > 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                <h2 className="text-lg font-semibold text-white">Live Now</h2>
                <span className="text-white/30 text-sm">
                  {liveGames.filter((g) => g.isLive).length} matches
                </span>
              </div>
              <div className="overflow-hidden" ref={liveNowRef}>
                <div className="flex gap-3">
                  {liveGames
                    .filter((g) => g.isLive)
                    .map((game) => {
                      const homeColor = getTeamColor(game.homeTeam);
                      const awayColor = getTeamColor(game.awayTeam);
                      const gradientColor =
                        homeColor?.color1 || awayColor?.color1;
                      const gc =
                        gradientColor &&
                        (gradientColor.startsWith("#")
                          ? gradientColor
                          : `#${gradientColor}`);

                      return (
                        <div
                          className="flex-none w-72 rounded-xl border border-white/[0.08] p-4 hover:border-white/[0.2] transition-all cursor-pointer group"
                          key={game.id}
                          role="button"
                          style={{
                            background: gc
                              ? `linear-gradient(135deg, ${gc}20 0%, rgba(255,255,255,0.03) 100%)`
                              : "rgba(255,255,255,0.03)",
                          }}
                          tabIndex={0}
                          onClick={() => handleGameClick(game)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleGameClick(game);
                          }}
                        >
                          {/* League + LIVE badge */}
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-white/30 text-[10px] font-medium uppercase tracking-wider truncate">
                              {game.league}
                            </span>
                            <span className="flex items-center gap-1.5 px-2 py-0.5 bg-red-500/15 border border-red-500/30 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                              <span className="text-red-400 text-[10px] font-bold uppercase">
                                Live
                              </span>
                            </span>
                          </div>
                          {/* Matchup */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {game.homeTeamLogo && (
                                <img
                                  alt=""
                                  className="w-8 h-8 object-contain flex-shrink-0"
                                  src={game.homeTeamLogo}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}
                              <span className="text-white text-sm font-medium truncate">
                                {game.homeTeam}
                              </span>
                            </div>
                            <span className="text-white font-bold text-lg mx-3 tabular-nums">
                              {game.homeScore ?? "-"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {game.awayTeamLogo && (
                                <img
                                  alt=""
                                  className="w-8 h-8 object-contain flex-shrink-0"
                                  src={game.awayTeamLogo}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}
                              <span className="text-white text-sm font-medium truncate">
                                {game.awayTeam}
                              </span>
                            </div>
                            <span className="text-white font-bold text-lg mx-3 tabular-nums">
                              {game.awayScore ?? "-"}
                            </span>
                          </div>
                          {/* Status */}
                          {game.statusText && (
                            <div className="mt-2 pt-2 border-t border-white/[0.06]">
                              <span className="text-white/30 text-[10px]">
                                {game.statusText}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* ─── MY LIVE GAMES ALERT ─── */}
          {myLiveGames.length > 0 && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/[0.08] border border-red-500/20">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-red-400 text-sm font-semibold">
                  Your teams are playing!
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                {myLiveGames.map((g) => (
                  <button
                    className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-white text-xs font-medium hover:bg-red-500/20 transition-all"
                    key={g.id}
                    onClick={() => handleGameClick(g)}
                  >
                    {g.homeTeam} {g.homeScore ?? "-"} - {g.awayScore ?? "-"}{" "}
                    {g.awayTeam}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ─── MY TEAMS RAIL ─── */}
          {myTeamsData.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  <i className="fas fa-heart text-red-400 mr-2 text-sm" />
                  My Teams
                </h2>
                <span className="text-white/20 text-sm">
                  {myTeamsData.length} teams
                </span>
              </div>
              <div className="overflow-hidden" ref={myTeamsRef}>
                <div className="flex gap-4">
                  {myTeamsData.map((team) => {
                    const tc = getTeamColor(team.name);
                    const c1 = tc?.color1
                      ? tc.color1.startsWith("#")
                        ? tc.color1
                        : `#${tc.color1}`
                      : null;
                    const nextGame = getNextGame(team.name);
                    const live = isTeamLive(team.name);

                    return (
                      <div
                        className="flex-none w-48 rounded-xl border border-white/[0.08] p-4 hover:border-white/[0.2] hover:scale-[1.02] transition-all cursor-pointer group relative overflow-hidden"
                        key={team.name}
                        role="button"
                        style={{
                          background: c1
                            ? `linear-gradient(180deg, ${c1}25 0%, rgba(255,255,255,0.03) 70%)`
                            : "rgba(255,255,255,0.04)",
                          borderTop: c1 ? `3px solid ${c1}` : undefined,
                        }}
                        tabIndex={0}
                        onClick={() => handleCardClick(team)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleTeamClick(team);
                        }}
                        onPointerDown={(e) =>
                          handleCardPointerDown(e, team.name)
                        }
                        onPointerLeave={handleCardPointerUp}
                        onPointerUp={handleCardPointerUp}
                      >
                        {/* LIVE indicator */}
                        {live && (
                          <div className="absolute top-2 left-2">
                            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/20 border border-red-500/30 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                              <span className="text-red-400 text-[9px] font-bold uppercase">
                                Live
                              </span>
                            </span>
                          </div>
                        )}
                        {/* Follow button */}
                        <div className="absolute top-2 right-2 z-10">
                          <FollowButton
                            entity={team.name}
                            size="sm"
                            type="team"
                            variant="icon"
                            onFollowChange={handleFollowChange}
                          />
                        </div>
                        <div className="flex flex-col items-center text-center pt-2">
                          <img
                            alt={team.name}
                            className="w-16 h-16 object-contain mb-3 group-hover:scale-110 transition-transform"
                            src={team.logo}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/64?text=?";
                            }}
                          />
                          <span className="text-white text-sm font-semibold leading-tight">
                            {team.name}
                          </span>
                          <span className="text-white/30 text-xs mt-1">
                            {team.league}
                          </span>
                          {/* Next game info */}
                          {nextGame && (
                            <div className="mt-2 pt-2 border-t border-white/[0.06] w-full">
                              <span className="text-white/25 text-[10px]">
                                {nextGame.isLive
                                  ? `vs ${nextGame.homeTeam?.toLowerCase() === team.name.toLowerCase() ? nextGame.awayTeam : nextGame.homeTeam}`
                                  : nextGame.eventDate
                                    ? `Next: ${nextGame.eventDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                                    : ""}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ─── RECOMMENDED TEAMS ─── */}
          {recommendedTeams.length > 0 && !debouncedQuery && (
            <div className="mb-10">
              <h2 className="text-lg font-semibold text-white mb-1">
                <i className="fas fa-star text-yellow-500/60 mr-2 text-sm" />
                You Might Also Like
              </h2>
              <p className="text-white/25 text-sm mb-4">
                Teams from leagues you follow
              </p>
              <div className="overflow-hidden" ref={recommendedRef}>
                <div className="flex gap-3">
                  {recommendedTeams.map((team) => {
                    const tc = getTeamColor(team.name);
                    const c1 = tc?.color1
                      ? tc.color1.startsWith("#")
                        ? tc.color1
                        : `#${tc.color1}`
                      : null;

                    return (
                      <div
                        className="flex-none w-36 rounded-xl border border-white/[0.06] p-3 hover:border-white/[0.15] hover:bg-white/[0.04] transition-all cursor-pointer group relative"
                        key={team.name}
                        role="button"
                        style={{
                          borderTop: c1 ? `2px solid ${c1}40` : undefined,
                        }}
                        tabIndex={0}
                        onClick={() => handleCardClick(team)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleTeamClick(team);
                        }}
                        onPointerDown={(e) =>
                          handleCardPointerDown(e, team.name)
                        }
                        onPointerLeave={handleCardPointerUp}
                        onPointerUp={handleCardPointerUp}
                      >
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                        <div
                          className="absolute top-1.5 right-1.5 z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FollowButton
                            entity={team.name}
                            size="sm"
                            type="team"
                            variant="icon"
                            onFollowChange={handleFollowChange}
                          />
                        </div>
                        <div className="flex flex-col items-center text-center">
                          <img
                            alt={team.name}
                            className="w-12 h-12 object-contain mb-2 group-hover:scale-105 transition-transform"
                            src={team.logo}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/48?text=?";
                            }}
                          />
                          <span className="text-white text-xs font-medium leading-tight">
                            {team.name}
                          </span>
                          <span className="text-white/20 text-[10px] mt-0.5">
                            {team.league}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ─── SPORT FILTER PILLS ─── */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Browse All Teams
            </h2>
            <div className="flex flex-wrap gap-2">
              {SPORT_FILTERS.map((filter) => {
                const isActive = activeSport === filter.sport;

                return (
                  <button
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      isActive
                        ? "bg-blue-500 text-white border border-blue-400 shadow-lg shadow-blue-500/20"
                        : "bg-white/[0.06] text-white/60 border border-white/[0.08] hover:bg-white/[0.1] hover:text-white/80"
                    }`}
                    key={filter.label}
                    tabIndex={0}
                    onClick={() => {
                      setActiveSport(filter.sport);
                      setShowAll(false);
                    }}
                  >
                    <i className={`${filter.icon} text-xs`} />
                    {filter.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-white/30 text-sm">
              {debouncedQuery
                ? `${filteredTeams.length} results for "${debouncedQuery}"`
                : `${filteredTeams.length} teams`}
              {activeSport && !debouncedQuery && (
                <span className="text-white/20">
                  {" "}
                  in {SPORT_FILTERS.find((f) => f.sport === activeSport)?.label}
                </span>
              )}
            </p>
          </div>

          {/* ─── TEAM GRID ─── */}
          {displayedTeams.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {displayedTeams.map((team) => {
                  const live = isTeamLive(team.name);
                  const colorStyle = getCardColorStyle(team.name);

                  return (
                    <div
                      className="rounded-xl border border-white/[0.08] p-4 hover:border-white/[0.2] hover:scale-[1.02] transition-all cursor-pointer group relative"
                      key={team.name}
                      role="button"
                      style={{
                        background:
                          colorStyle.background || "rgba(255,255,255,0.03)",
                        borderTop: colorStyle.borderTop || undefined,
                      }}
                      tabIndex={0}
                      onClick={() => handleCardClick(team)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleTeamClick(team);
                      }}
                      onPointerDown={(e) => handleCardPointerDown(e, team.name)}
                      onPointerLeave={handleCardPointerUp}
                      onPointerUp={handleCardPointerUp}
                    >
                      {/* LIVE badge */}
                      {live && (
                        <div className="absolute top-2 left-2">
                          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/20 border border-red-500/30 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                            <span className="text-red-400 text-[9px] font-bold uppercase">
                              Live
                            </span>
                          </span>
                        </div>
                      )}
                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                      <div
                        className="absolute top-2 right-2 z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FollowButton
                          entity={team.name}
                          size="sm"
                          type="team"
                          variant="icon"
                          onFollowChange={handleFollowChange}
                        />
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <img
                          alt={team.name}
                          className="w-16 h-16 object-contain mb-3 group-hover:scale-110 transition-transform"
                          src={team.logo}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/64?text=?";
                          }}
                        />
                        <span className="text-white text-sm font-medium leading-tight">
                          {team.name}
                        </span>
                        <span className="text-white/30 text-xs mt-1">
                          {team.league}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Show All / Show Less */}
              {filteredTeams.length > INITIAL_SHOW && (
                <div className="flex justify-center mt-8">
                  <button
                    className="px-6 py-2.5 rounded-lg bg-white/[0.06] border border-white/[0.1] text-sm text-white/60 hover:bg-white/[0.1] hover:text-white/80 hover:border-white/20 font-medium transition-all"
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll
                      ? "Show Less"
                      : `Show All ${filteredTeams.length} Teams`}
                    <i
                      className={`fas fa-chevron-${showAll ? "up" : "down"} ml-2 text-xs`}
                    />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="fas fa-search text-4xl text-white/10 mb-4" />
              <p className="text-white/40 text-lg mb-2">No teams found</p>
              <p className="text-white/20 text-sm">
                {debouncedQuery
                  ? "Try a different search term"
                  : "No teams match the current filter"}
              </p>
              {(debouncedQuery || activeSport) && (
                <button
                  className="mt-4 px-4 py-2 rounded-lg bg-white/[0.06] border border-white/[0.1] text-sm text-white/50 hover:bg-white/[0.1] hover:text-white/70 transition-all"
                  onClick={() => {
                    setSearchQuery("");
                    setActiveSport(null);
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Context Menu (long-press) */}
      {contextMenu && (
        <div
          className="fixed inset-0 z-[9999]"
          onPointerDown={(e) => {
            e.stopPropagation();
            setContextMenu(null);
          }}
        >
          <div
            className="absolute bg-gray-900/95 backdrop-blur-xl border border-white/[0.12] rounded-xl shadow-2xl shadow-black/50 py-1.5 min-w-[180px]"
            style={{
              left: Math.min(contextMenu.x, window.innerWidth - 200),
              top: Math.min(contextMenu.y, window.innerHeight - 160),
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* Team name header */}
            <div className="px-4 py-2 border-b border-white/[0.06]">
              <span className="text-white text-sm font-semibold">
                {contextMenu.teamName}
              </span>
            </div>

            {/* Follow / Unfollow */}
            <button
              className="w-full px-4 py-2.5 text-left text-sm hover:bg-white/[0.08] transition-colors flex items-center gap-3"
              onClick={handleContextFollow}
            >
              {isTeamFollowed(contextMenu.teamName) ? (
                <>
                  <i className="fas fa-heart-broken text-red-400 w-4 text-center" />
                  <span className="text-red-400">Unfollow</span>
                </>
              ) : (
                <>
                  <i className="fas fa-heart text-pink-400 w-4 text-center" />
                  <span className="text-white/80">Follow</span>
                </>
              )}
            </button>

            {/* View Team */}
            <button
              className="w-full px-4 py-2.5 text-left text-sm text-white/80 hover:bg-white/[0.08] transition-colors flex items-center gap-3"
              onClick={handleContextViewTeam}
            >
              <i className="fas fa-arrow-right w-4 text-center text-blue-400" />
              <span>View Team</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebPlayerSportsTeams;
